import React, { useEffect, useState } from "react";

import {
  Box,
  Typography,
  Paper,
  Chip,
  Avatar,
  CircularProgress
} from "@mui/material";

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell
} from "recharts";

import { getCapacityOverview } from "../../services/reportService";
import { initialsOf } from "../../utils/commentUtils";
import { avatarColorOf } from "../../utils/avatarUtils";
import uiConfig from "../../config/uiConfig";

const PALETTE = uiConfig.chartPalette;
const BUCKET_COLORS = {
  "0-25%": PALETTE[0],
  "26-50%": PALETTE[1],
  "51-75%": PALETTE[2],
  "76-100%": PALETTE[3],
  "Over 100%": PALETTE[4]
};

function EmployeeRow({ employee }) {
  const overallocated = employee.total_capacity_percent > 100;

  return (
    <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ py: 0.8 }}>
      <Box display="flex" alignItems="center" gap={1}>
        <Avatar sx={{ width: 26, height: 26, fontSize: 11, bgcolor: avatarColorOf(employee.employee_name) }}>
          {initialsOf(employee.employee_name)}
        </Avatar>
        <Typography sx={{ fontSize: 13, color: "text.primary" }}>{employee.employee_name}</Typography>
      </Box>

      <Chip
        size="small"
        label={`${employee.total_capacity_percent}%`}
        sx={{
          fontWeight: 700,
          fontSize: 11,
          background: overallocated ? "#fee2e2" : "#eff6ff",
          color: overallocated ? "#dc2626" : "#2563eb"
        }}
      />
    </Box>
  );
}

function CapacityOverviewReport({ departmentId = null, startDate = null, endDate = null }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedBucket, setSelectedBucket] = useState(null);

  const BUCKETS = [
    { label: "All",     key: null,      min: null,  max: null  },
    { label: "0%",      key: "0",       min: 0,     max: 0     },
    { label: "1–25%",   key: "1-25",    min: 1,     max: 25    },
    { label: "26–50%",  key: "26-50",   min: 26,    max: 50    },
    { label: "51–75%",  key: "51-75",   min: 51,    max: 75    },
    { label: "76–100%", key: "76-100",  min: 76,    max: 100   },
    { label: ">100%",   key: ">100",    min: 101,   max: null  },
  ];

  useEffect(() => {
    setLoading(true);
    const bucket = BUCKETS.find((b) => b.key === selectedBucket) || BUCKETS[0];
    getCapacityOverview(departmentId, startDate, endDate, bucket.min, bucket.max)
      .then(setData)
      .catch((error) => console.error("Capacity Overview Error:", error))
      .finally(() => setLoading(false));
  }, [departmentId, startDate, endDate, selectedBucket]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <Box height={330} display="flex" justifyContent="center" alignItems="center">
        <CircularProgress />
      </Box>
    );
  }

  const distribution = data?.distribution || [];
  const allEmployees = data?.top_allocated || [];
  const topAllocated = allEmployees.slice(0, 10);
  const bench = data?.bench || [];
  const activeBucket = BUCKETS.find((b) => b.key === selectedBucket) || BUCKETS[0];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Paper
        sx={{
          p: 2.5,
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          boxShadow: "0 2px 8px rgba(0,0,0,0.04)"
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: "text.primary" }}>
            Capacity &amp; Resource Overview
          </Typography>

          <Chip
            size="small"
            label={`${data?.bench_count || 0} on bench`}
            sx={{ fontWeight: 700, background: "#fef3c7", color: "#b45309" }}
          />
        </Box>

        <Typography sx={{ fontSize: 13, fontWeight: 600, color: "text.secondary", mb: 1 }}>
          Capacity Distribution
        </Typography>

        <ResponsiveContainer width="100%" height={230}>
          <BarChart data={distribution}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" fontSize={11} />
            <YAxis allowDecimals={false} fontSize={12} />
            <Tooltip />
            <Bar dataKey="value" radius={[6, 6, 0, 0]}>
              {distribution.map((entry) => (
                <Cell key={entry.name} fill={BUCKET_COLORS[entry.name] || PALETTE[5]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Paper>

      {/* Utilization bucket filter chips */}
      <Box display="flex" gap={0.8} flexWrap="wrap" alignItems="center">
        <Typography fontSize={12} fontWeight={600} color="text.secondary" mr={0.5}>Filter by utilization:</Typography>
        {BUCKETS.map((b) => (
          <Chip
            key={b.key ?? "all"}
            label={b.label}
            size="small"
            onClick={() => setSelectedBucket(b.key)}
            sx={{
              cursor: "pointer",
              fontWeight: selectedBucket === b.key ? 700 : 500,
              bgcolor: selectedBucket === b.key ? "primary.main" : "action.hover",
              color: selectedBucket === b.key ? "#fff" : "text.primary",
              "&:hover": { bgcolor: selectedBucket === b.key ? "primary.dark" : "action.selected" },
            }}
          />
        ))}
      </Box>

      {/* Filtered view — single list when a bucket is selected */}
      {selectedBucket ? (
        <Paper sx={{ p: 2.5, borderRadius: 3, border: "1px solid", borderColor: "divider", boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
            <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
              {activeBucket.label} utilization
            </Typography>
            <Chip size="small" label={`${allEmployees.length} employee(s)`} sx={{ fontWeight: 600, bgcolor: "#eff6ff", color: "#2563eb" }} />
          </Box>
          {allEmployees.length === 0 ? (
            <Typography fontSize={12.5} color="text.secondary">No employees in this range</Typography>
          ) : (
            allEmployees.map((emp) => <EmployeeRow key={emp.employee_id} employee={emp} />)
          )}
        </Paper>
      ) : (
        <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: 2
        }}
      >
        <Paper
          sx={{
            p: 2.5,
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
            boxShadow: "0 2px 8px rgba(0,0,0,0.04)"
          }}
        >
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: "text.primary", mb: 1.5 }}>
            Top 10 Allocated
          </Typography>

          {topAllocated.length === 0 && (
            <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>No data</Typography>
          )}

          {topAllocated.map((employee) => (
            <EmployeeRow key={employee.employee_id} employee={employee} />
          ))}
        </Paper>

        <Paper
          sx={{
            p: 2.5,
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
            boxShadow: "0 2px 8px rgba(0,0,0,0.04)"
          }}
        >
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: "text.primary", mb: 1.5 }}>
            Bench (Under-allocated)
          </Typography>

          {bench.length === 0 && (
            <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>Everyone is allocated</Typography>
          )}

          {bench.map((employee) => (
            <EmployeeRow key={employee.employee_id} employee={employee} />
          ))}
        </Paper>
      </Box>
      )}
    </Box>
  );
}

export default CapacityOverviewReport;
